import React, { useState, useEffect, useCallback } from 'react';
import { images } from "../constants";
import CommentModal from '../utils/CommentModal';
import Comment from './Comment';
import EditDeleteModal from '../utils/EditDeleteModal';
import LikeButton from '../utils/LikeButton';
import timeSince from '../utils/TimeStamp';
import { useCurrentUser } from '../UserProvider/UserProvider';
import { baseUrl, getApiUrl } from '../shared';
import axios from 'axios';
import EditPostModal from '../utils/EditPostModal';
import ShareModal from '../utils/ShareModal';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon as SparklesIconSolid } from '@heroicons/react/24/solid';

const PostList = ({ posts, onAddPost, onOpenPostModal }) => {
    const { user, fetchUser } = useCurrentUser();
    const navigate = useNavigate();
    const [otherUser, setOtherUser] = useState(null);
    // console.log("otherUser", otherUser);

    useEffect(() => {
        fetchUser();
    }, []);

    const handleNavigateToProfile = async (authorId) => {
        try {
            const response = await axios.get(`${baseUrl}api/users/${authorId}/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access')}`,
                },
            });
            if (authorId !== user.id) {
                setOtherUser(response.data);
                console.log('Other user reponse:', response.data);
            }

        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    useEffect(() => {
        if (otherUser) {
            navigate(`/profile/${otherUser.id}`, { state: { otherUser } });
        }
    }, [otherUser])
    const [postLikes, setPostLikes] = useState({}); // State to track likes for each post
    const [postLikeCounts, setPostLikeCounts] = useState({}); // State to track like counts per post

    const updateLikeStatus = useCallback((postId, isLiked, newCount) => {
        setPostLikes((prevPostLikes) => ({ ...prevPostLikes, [postId]: isLiked }));
        if (newCount !== undefined) {
            setPostLikeCounts(prev => ({ ...prev, [postId]: newCount }));
        }
    }, []);

    const [modalIsOpen, setModalIsOpen] = useState(null);
    const [postIdToDelete, setPostIdToDelete] = useState(null);
    const [postIdToEdit, setPostIdToEdit] = useState(null);
    const [postToEdit, setPostToEdit] = useState(null);

    const handleOpenModal = (postId) => {
        setModalIsOpen(postId);
    };

    const handleEditPost = async (postId) => {
        try {
            const response = await axios.get(`${baseUrl}api/posts/${postId}/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access')}`,
                },
            });
            setPostIdToEdit(postId);
            setPostToEdit(response.data);
            //console.log('Post to edit:', response.data);
            setModalIsOpen(true);
        } catch (error) {
            console.error('Error fetching post:', error);
        }
    };

    const handleCloseModal = () => {
        setModalIsOpen(false);
        setPostToEdit(null);
    };


    const handleDeletePost = (postId) => {
        setPostIdToDelete(postId);
        console.log('Post to delete:', postId);
        handleCloseModal();
    };

    const handleConfirmDelete = async () => {
        console.log('Deleting post');
        try {
            await axios.delete(`${baseUrl}api/posts/${postIdToDelete}/delete/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access')}`,
                },
            });
            onAddPost(false); // Update the post list when a post is deleted
            // alert("Post deleted successfully!");
        } catch (error) {
            console.error('Error deleting post:', error);
        } finally {
            setPostIdToDelete(null);
        }
    };

    const [lastUpdatedComment, setLastUpdatedComment] = useState(null);

    const updateComments = useCallback((time) => {
        setLastUpdatedComment(time);
        onAddPost(false); // Update the post list when a comment is added
    }, [onAddPost]);



    const [visibleComments, setVisibleComments] = useState({});

    const toggleComments = (postId) => {
        setVisibleComments((prev) => ({
            ...prev,
            [postId]: !prev[postId]
        }));
    };

    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [postToShare, setPostToShare] = useState(null);
    const [expandedPosts, setExpandedPosts] = useState({});

    const handleShare = (post) => {
        if (!user) {
            navigate('/login');
            return;
        }
        setPostToShare(post);
        setShareModalOpen(true);
    };

    const toggleExpandPost = (postId) => {
        setExpandedPosts((prev) => ({
            ...prev,
            [postId]: !prev[postId]
        }));
    };


    return (
        <div className="container mx-auto p-4">
            <div className="flex flex-col items-center">
                {posts?.map((item) => {
                    if (item.isNews) {
                        return (
                            <div key={`news-${item.article_id}`} className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-2xl w-full mb-6 relative overflow-hidden group">
                                <div className="p-5 flex items-center gap-2 border-b border-gray-50">
                                    <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded">News</span>
                                    <span className="text-gray-400 text-xs">Suggested for you</span>
                                </div>
                                <a href={item.link} target="_blank" rel="noopener noreferrer" className="block">
                                    <div className="relative">
                                        <img
                                            src={item.image_url || images.toss}
                                            alt={item.title}
                                            className="w-full h-48 sm:h-64 object-cover"
                                            onError={(e) => { e.target.onerror = null; e.target.src = images.toss; }}
                                        />
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                                            {/* Gradient overlay for readability if needed */}
                                        </div>
                                    </div>
                                    <div className="p-6 bg-gray-50 hover:bg-gray-100 transition-colors">
                                        <p className="text-gray-500 text-xs uppercase tracking-wide mb-1 truncate">{new URL(item.link).hostname.replace('www.', '')}</p>
                                        <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight group-hover:text-blue-600 transition-colors">{item.title}</h3>
                                        <p className="text-gray-600 text-sm overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{item.description}</p>
                                    </div>
                                </a>
                                <div className="px-6 py-3 border-t border-gray-100 flex justify-between items-center bg-white">
                                    <button
                                        onClick={() => handleShare(item)}
                                        className="text-gray-500 hover:bg-gray-100 p-2 rounded-full transition-colors flex items-center gap-2"
                                        title="Share Article"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path></svg>
                                        <span className="text-sm font-medium hidden sm:inline">Share</span>
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (!user) {
                                                navigate('/login');
                                            } else {
                                                onOpenPostModal && onOpenPostModal(`${item.title}\n${item.link}\n\nCheck this out!`);
                                            }
                                        }}
                                        className="text-gray-500 hover:bg-gray-100 p-2 rounded-full transition-colors flex items-center gap-2"
                                        title="Discuss this Article"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>
                                        <span className="text-sm font-medium hidden sm:inline">Discuss</span>
                                    </button>
                                </div>
                            </div>
                        );
                    }

                    const post = item;

                    return (
                        <div key={post.id} className="bg-white/90 backdrop-blur-md text-slate-800 rounded-2xl shadow-xl border border-white/30 max-w-2xl w-full mb-6 relative overflow-hidden">
                            {post.is_announcement && (
                                <div className="bg-[#17717d] w-full py-1.5 px-6 flex items-center justify-between shadow-sm z-10 relative">
                                    <div className="flex items-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-white">
                                            <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c1.66 0 3.223.495 4.525 1.353a.75.75 0 001-.707V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
                                        </svg>
                                        <span className="text-[10px] font-bold text-white uppercase tracking-wider">Official Announcement</span>
                                    </div>
                                    <SparklesIconSolid className="w-3.5 h-3.5 text-[#A855F7]" />
                                </div>
                            )}
                            {/* Header */}
                            <div className="p-6 flex items-center justify-between">
                                <div className="flex items-center space-x-3 cursor-pointer" onClick={() => {
                                    if (!user) {
                                        navigate('/login');
                                    } else if (!post.author) {
                                        // System posts don't have a profile to navigate to
                                        return;
                                    } else if (post.author.id !== user.id) {
                                        handleNavigateToProfile(post.author.id)
                                    } else {
                                        navigate('/profile/')
                                    }
                                }}>
                                    <img src={(() => {
                                        const postAuthorImage = post.author?.image;
                                        if (!postAuthorImage) return images.profile;
                                        if (postAuthorImage.startsWith('http')) return postAuthorImage;
                                        return getApiUrl(postAuthorImage);
                                    })()}
                                        alt="User Avatar"
                                        className="w-10 h-10 rounded-full object-cover border border-gray-100 shadow-sm"
                                    />
                                    <div>
                                        <div className="flex items-center gap-1.5">
                                            <p className="text-slate-900 font-semibold text-sm hover:underline">{post.author?.username || 'Official'}</p>
                                            {post.author?.role === 'college_staff' && post.author?.is_verified && (
                                                <span className="flex items-center gap-0.5 bg-teal-50 text-primary text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-teal-100 uppercase tracking-tighter">
                                                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 12c0 3.839 2.302 7.144 5.575 8.556a12.017 12.017 0 0 0 12.85 0c3.273-1.412 5.575-4.717 5.575-8.556 0-3.14-1.382-5.957-3.598-7.882M12 21V10.332" /></svg>
                                                    Verified Institution
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-slate-400 text-xs">{timeSince(post.created_at)}</p>
                                        {post.category && post.category !== 'general' && (
                                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${{
                                                acceptance: 'bg-green-50 text-green-600 border border-green-200',
                                                essay_help: 'bg-amber-50 text-amber-600 border border-amber-200',
                                                campus_tours: 'bg-blue-50 text-blue-600 border border-blue-200',
                                                financial_aid: 'bg-emerald-50 text-emerald-600 border border-emerald-200',
                                                test_prep: 'bg-violet-50 text-violet-600 border border-violet-200',
                                                advice: 'bg-orange-50 text-orange-600 border border-orange-200',
                                            }[post.category] || 'bg-gray-50 text-gray-500 border border-gray-200'
                                                }`}>
                                                {post.category_display || post.category}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-gray-400 cursor-pointer relative">
                                    {post.author?.id === user?.id && user && (
                                        <button type="button" onClick={(e) => { e.stopPropagation(); handleOpenModal(post.id); }} className="hover:bg-gray-100 rounded-full p-2 transition-colors">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="7" r="1" />
                                                <circle cx="12" cy="12" r="1" />
                                                <circle cx="12" cy="17" r="1" />
                                            </svg>
                                        </button>
                                    )}
                                    <EditDeleteModal
                                        isOpen={modalIsOpen === post.id}
                                        onClose={handleCloseModal}
                                        onEdit={() => handleEditPost(post.id)}
                                        onDelete={() => handleDeletePost(post.id)}
                                    />
                                    {postIdToEdit &&
                                        <EditPostModal post={postToEdit} isOpen={modalIsOpen} onClose={handleCloseModal} onAddPost={onAddPost} />
                                    }
                                </div>
                            </div>

                            {/* Inner Content Container */}
                            <div className="mx-6 mb-4 bg-slate-100/85 rounded-xl border border-slate-200/80 overflow-hidden text-slate-700">
                                {/* Content */}
                                <div className="p-4">
                                    {post.title && <h3 className="text-lg font-bold text-slate-900 mb-1 leading-snug">{post.title}</h3>}
                                    {(() => {
                                        const isExpanded = expandedPosts[post.id];
                                        const content = post.content || "";
                                        const lineCount = content.split('\n').length;
                                        const shouldTruncate = content.length > 450 || lineCount > 10;
                                        const displayContent = shouldTruncate && !isExpanded
                                            ? (lineCount > 10
                                                ? content.split('\n').slice(0, 10).join('\n') + '...'
                                                : content.substring(0, 450) + '...')
                                            : content;

                                        return (
                                            <>
                                                <p className="text-slate-700 text-base leading-relaxed whitespace-pre-wrap">{displayContent}</p>
                                                {shouldTruncate && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); toggleExpandPost(post.id); }}
                                                        className="text-[#17717d] hover:text-[#24adbf] text-sm font-semibold mt-2 focus:outline-none"
                                                    >
                                                        {isExpanded ? 'See less' : 'See more...'}
                                                    </button>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Image */}
                                {post.image && (
                                    <div className="w-full relative group">
                                        <img
                                            src={post.image.startsWith('http') ? post.image : `${baseUrl}${post.image.startsWith('/') ? post.image.substring(1) : post.image}`}
                                            alt="Post Content"
                                            className="w-full h-auto max-h-[380px] object-cover transition-transform duration-700 hover:scale-[1.01]"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Stats Row */}
                            <div className="px-6 py-3 flex items-center justify-between text-sm text-slate-400 border-b border-slate-100/50">
                                <div className="flex items-center gap-1">
                                    {(postLikeCounts[post.id] !== undefined ? postLikeCounts[post.id] : post.likes_count) > 0 && (
                                        <>
                                            <div className="p-1 bg-primary rounded-full">
                                                <svg className="w-3 h-3 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-1.91l-.01-.01L23 10z" /></svg>
                                            </div>
                                            <span>{postLikeCounts[post.id] !== undefined ? postLikeCounts[post.id] : post.likes_count}</span>
                                        </>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="hover:underline cursor-pointer focus:outline-none"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleComments(post.id); }}
                                >
                                    {post.comments_count >= 0 ? `${post.comments_count} Comments` : ''}
                                </button>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex items-center justify-between px-6 py-2 border-b border-slate-100/50">
                                {user ? (
                                    <LikeButton
                                        contentType="post"
                                        objectId={post.id}
                                        onLikeStatusChange={updateLikeStatus}
                                        className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition-colors ${postLikes[post.id] ? 'text-[#17717d] font-semibold' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800'}`}
                                    >
                                        <svg className={`w-5 h-5 ${postLikes[post.id] ? 'fill-current' : 'fill-none stroke-current'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                                        </svg>
                                        <span>Like</span>
                                    </LikeButton>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/login'); }}
                                        className="flex-1 flex items-center justify-center gap-2 py-2 text-slate-500 hover:bg-slate-50 hover:text-slate-800 rounded-lg transition-colors font-medium text-sm"
                                    >
                                        <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                                        </svg>
                                        <span>Like</span>
                                    </button>
                                )}

                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleComments(post.id); }}
                                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors font-medium text-sm"
                                >
                                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    <span>Comment</span>
                                </button>

                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare(post); }}
                                    className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition-colors font-medium text-sm"
                                >
                                    <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                    <span>Share</span>
                                </button>
                            </div>

                            {/* Comments Section */}
                            {visibleComments[post.id] && (
                                <div className="px-6 py-4 bg-slate-100/90 rounded-b-2xl border-t border-slate-200/60" onClick={(e) => e.stopPropagation()}>
                                    {user && (
                                        <div className="mb-6">
                                            <CommentModal postId={post.id} onAddComment={updateComments} />
                                        </div>
                                    )}
                                    <Comment postId={post.id} lastUpdatedComment={lastUpdatedComment} onAddPost={onAddPost} user={user} />
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
            {postIdToDelete && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-500 bg-opacity-75">
                    <div className="bg-primary border border-black text-black px-4 py-3 rounded relative" role="alert">
                        <strong className="font-bold">Confirmation Required</strong><br />
                        <span className="block sm:inline">Are you sure you want to delete this {posts.find(post => post.id === postIdToDelete)?.itemType}?</span>
                        <div className="flex justify-end mt-2">
                            <button className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded mr-2" onClick={handleConfirmDelete}>Confirm</button>
                            <button className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-4 rounded" onClick={() => setPostIdToDelete(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <ShareModal
                isOpen={shareModalOpen}
                onClose={() => setShareModalOpen(false)}
                postUrl={postToShare ? (postToShare.isNews ? postToShare.link : `${window.location.origin}/posts/${postToShare.id}`) : window.location.href} // Handle news links
            />

        </div>
    );
}

export default PostList;
